import React, { useContext, useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import Grid from '@mui/material/Grid';
import { AuthContext } from '../reducers/Auth/AuthContext';
import { PlaceStoreContext } from '../reducers/Place/PlaceContext';
import PublishButton from './PublishButton';
import Button from '@mui/material/Button';
import { EditField } from './EditField';
import { EditNested } from './EditNested';
import { EditImage } from './EditImage';
import { EditDetails } from './EditDetails';
import { EditChecked } from './EditChecked';
import { EditCategory } from './EditCategory';
import { EditYesNo } from './EditYesNo';
import BarLoader from 'react-spinners/BarLoader';
import { updateCategoryImage } from '../apis/updateCategoryImage';
import { updateCategory } from '../apis/updateCategory';
import { addNewCategory } from '../apis/addNewCategory';
import { deleteCategory } from '../apis/deleteCategory';
import { removeImageFromBucket } from '../apis/removeImageFromBucket';
import { uuid } from '../utils/uuid';
import AreYouSureYesNo from './AreYouSureYesNo';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { COLORS } from '../utils/colors';
import config from '../utils/config';

const ManageCategory = (props) => {
  const { categoryId, nested, serviceId, close, restricted } = props;
  const [authState] = useContext(AuthContext);
  const [file, setFile] = useState(null);
  const [placeState] = useContext(PlaceStoreContext);
  const [initialData, setInitialData] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [categoryDelete, setCategoryDelete] = useState({
    visible: false,
    isDeleting: false,
  });

  const {
    products: { productId__product },
    categories: {
      categoryId__category,
      index: indexCategories,
      gorupCategoryId__categories,
      categoryId__products,
    },
    services: { serviceId__service },
  } = placeState;
  const {
    user: { placeId, role },
  } = authState;

  const isBasic = role === 'basic';

  const category = categoryId__category[categoryId];
  const service = serviceId__service[serviceId];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!category) return;

    const newCategory = category.id === 'new';

    let categoryHasProducts = categoryId__products[categoryId]?.length > 0;
    let categoryHasNestedCategories = gorupCategoryId__categories[categoryId]?.length > 0;

    const { group__categoryIds } = indexCategories;

    let foundItems = [];

    if (categoryHasProducts) {
      const products = categoryId__products[categoryId];
      const orderedProducts = [];
      for (const product of products) {
        orderedProducts[product.position] = product;
      }
      foundItems = orderedProducts.filter((product) => product && product.id);
    } else if (categoryHasNestedCategories) {
      const nestedCategories = {};
      for (const id in categoryId__category) {
        if (categoryId__category[id].nested === categoryId) {
          nestedCategories[id] = true;
        }
      }

      const categories = [];
      for (const categoryId in categoryId__category) {
        if (newCategory || !nestedCategories[categoryId]) continue;
        const isGroupCategory = categoryId__category[categoryId].group;
        const categoryProducts = isGroupCategory
          ? group__categoryIds[categoryId]
          : indexCategories.categoryId__productsId[categoryId];
        const productId = categoryProducts
          ? categoryProducts[Math.floor(Math.random() * categoryProducts.length)]
          : null;
        categoryId__category[categoryId].randomImageFromProducts = productId
          ? `${config.uri.bucket}/${placeId}/high/${
              productId__product[productId].id
            }.jpg?time=${new Date().getTime()}`
          : null;
        const categoryWithRandomImage = categoryId__category[categoryId];
        categories[Math.abs(categoryWithRandomImage.position)] = categoryWithRandomImage;
      }
      foundItems = categories.filter((category) => !!category);
    }

    let image = null;
    if (category.has_image) {
      image = `${config.uri.bucket}/${placeId}/high/${
        category.id
      }.jpg?time=${new Date().getTime()}`;
    }

    const imageDetails = {
      image,
      local: false,
    };

    const newGroupCategory = nested ? nested : false;
    const defaultGroup = category.group ? 'group' : category.nested === null ? 'normal' : 'nested';

    const initialValues = {
      id: category.id,
      active: category.active,
      name: category.name,
      code: category.code || '',
      description: category.description || '',
      image: imageDetails,
      serviceId: newCategory ? serviceId : category.service_id,
      showable: newCategory ? false : category.showable,
      hasSubCategories: newGroupCategory ? false : category.group,
      group: newGroupCategory ? 'nested' : defaultGroup,
      nested: newGroupCategory ? newGroupCategory : category.nested || '',
      compact: Boolean(category.compact),
      top: category.top,
    };

    setInitialData({
      initialValues,
      imageUrl: imageDetails.image,
      categoryHasProducts,
      categoryHasNestedCategories,
    });
    if (categoryDelete.visible) {
      setCategoryDelete({ visible: false, isDeleting: false });
      close();
    }
  }, [categoryId, placeState]);

  const handleCategoryDelete = () => {
    setCategoryDelete({ visible: true, isDeleting: false });
  };

  const categoryOnNo = () => {
    setCategoryDelete({ visible: false, isDeleting: false });
  };

  const categoryOnYes = async () => {
    setCategoryDelete({ visible: true, isDeleting: true });
    await deleteCategory(placeId, categoryId);
    close();
  };

  if (!categoryId__category[categoryId] || initialData === null) {
    return null;
  }

  if (categoryDelete.isDeleting) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: `calc(90vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
          height: '80vh',
          margin: '0 auto',
        }}
      >
        <Grid container spacing={2} style={{ padding: '16px' }}>
          <Grid item xs={12}>
            <AreYouSureYesNo
              onNo={categoryOnNo}
              onYes={categoryOnYes}
              loading={categoryDelete.isDeleting}
              loadingMessage="Deleting category ..."
              warning="THIS CATEGORY AND ALL THE PRODUCTS IN IT WILL BE DELETED!"
            />
          </Grid>
        </Grid>
      </div>
    );
  }

  return (
    <Grid container style={{ marginTop: '40px' }}>
      <Grid item xs={12}>
        <Formik
          enableReinitialize
          initialValues={initialData.initialValues}
          validationSchema={Yup.object({
            active: Yup.boolean().required('Required'),
            name: Yup.string()
              .min(3, 'Must have at least 3 chars')
              .max(64, 'Must be 64 characters or less')
              .required('Required'),
            code: Yup.string().max(36, 'Must be 36 characters or less'),
            description: Yup.string().max(2048, 'Must be 2048 characters or less'),
            showable: Yup.boolean().required('Required'),
            compact: Yup.boolean().required('Required'),
            nested: Yup.string()
              .when('group', {
                is: 'normal',
                then: Yup.string(),
              })
              .when('group', {
                is: 'group',
                then: Yup.string(),
              })
              .when('group', {
                is: 'nested',
                then: Yup.string().min(1).required('Required'),
              }),
            group: Yup.string().required('Required'),
            top: Yup.boolean().required('Required'),
          })}
          onSubmit={async (values, { setSubmitting }) => {
            if (!placeId) return;
            const newCategory = category.id === 'new';
            setSubmitting(true);

            let hasImage = false;

            const selectedCategoryId = newCategory ? uuid() : category.id;
            if (file) {
              hasImage = true;
              console.log('Updating Image');
              await updateCategoryImage(placeId, selectedCategoryId, file);
              console.log('Done updating image');
            }

            if (category.has_image && removeImage && !newCategory) {
              console.log('Removing image from category');
              await removeImageFromBucket(placeId, 'category', category.id);
              console.log('Done removing image from category');
            }

            if (newCategory) {
              console.log('Adding New Category');
              values.hasImage = hasImage;
              await addNewCategory(placeId, selectedCategoryId, { values });
              console.log('Done adding new category');
            } else {
              await updateCategory(placeId, categoryId, values);
              console.log('Done updating category');
            }

            if (file) {
              setFile(null);
            }
            setSubmitting(false);
            close();
          }}
        >
          {({ values, setFieldValue, isSubmitting }) => {
            if (isSubmitting)
              return (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: `calc(100vw - ${Math.round((window.innerWidth * 8) / 100)}px)`,
                    height: '80vh',
                    margin: '0 auto',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <p style={{ margin: 0, paddingBottom: '20px', fontWeight: 'bold' }}>
                      SAVING CHANGES AND PUBLISHING ...
                    </p>
                    <BarLoader height={4} width={100} color="#000000" loading={true} />
                  </div>
                </div>
              );
            const submitDisabled =
              isSubmitting ||
              (file === null && _.isEqual(initialData.initialValues, values) && !removeImage);
            const hasSubElements =
              initialData.categoryHasProducts || initialData.categoryHasNestedCategories;

            const TypeOfCategory = serviceId
              ? `NEW ${service.name.toUpperCase()} CATEGORY`
              : 'NEW CATEGORY';
            const title =
              categoryId === 'new' && nested
                ? `NEW SUB CATEGORY FOR ${categoryId__category[nested].name.toUpperCase()}`
                : categoryId !== 'new'
                ? category.name.toUpperCase()
                : TypeOfCategory;
            return (
              <div>
                <Form encType="multipart/form-data">
                  <Grid container spacing={2} justify="center" style={{ maxWidth: '500px' }}>
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          padding: 0,
                          fontWeight: 'bold',
                          color: COLORS.INFO,
                        }}
                      >
                        {title}
                      </p>
                      <PublishButton name="PUBLISH" disabled={submitDisabled} />
                    </Grid>
                    <Grid item xs={12}>
                      <EditYesNo label="Active" name="active" />
                    </Grid>

                    {!isBasic && <Grid item xs={12}>
                      <EditImage
                        initialImage={initialData.imageUrl}
                        image={values.image}
                        removeImage={removeImage}
                        setRemoveImage={setRemoveImage}
                        setFieldValue={setFieldValue}
                        setFile={setFile}
                        file={file}
                        height="165px"
                      />
                    </Grid>}
                    <Grid item xs={12}>
                      <EditChecked
                        type="checkbox"
                        label="Display category on the main page"
                        name="top"
                      />
                      <EditChecked type="checkbox" label="Compact products" name="compact" />
                    </Grid>
                    <Grid item xs={12}>
                      <EditField
                        label="Name (64 max characters)"
                        name="name"
                        style={{ paddingBottom: '15px' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <EditField
                        label="Code (36 max characters)"
                        name="code"
                        style={{ paddingBottom: '15px' }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <EditDetails label="Description (2048 max characters)" name="description" />
                    </Grid>
                    {!restricted && (
                      <Grid item xs={12}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <EditNested
                            label="Category Type"
                            name="group"
                            style={{
                              flex: 1,
                              marginRight:
                                hasSubElements || values.group === 'nested' ? '0px' : '10px',
                            }}
                            config={{
                              categoryHasProducts: initialData.categoryHasProducts,
                              categoryHasNestedCategories: initialData.categoryHasNestedCategories,
                            }}
                          />
                          {!(hasSubElements || values.group === 'nested') && (
                            <EditChecked
                              type="checkbox"
                              label="Has Sub Categories"
                              name="hasSubCategories"
                              style={{ flex: 2 }}
                              disabled={hasSubElements || values.group === 'nested'}
                            />
                          )}
                        </div>
                      </Grid>
                    )}
                    {values.group === 'nested' && !restricted && (
                      <Grid item xs={12}>
                        <EditCategory label="Select Main Category" name="nested" multiple={false} />
                      </Grid>
                    )}
                    {values.group === 'nested' && (
                      <Grid item xs={12}>
                        <EditChecked
                          type="checkbox"
                          label="Showable also as category"
                          name="showable"
                        />
                      </Grid>
                    )}
                    {categoryId !== 'new' && (
                      <Grid item xs={12} style={{ marginTop: 16 }}>
                        {!categoryDelete.visible ? (
                          <Button variant="contained" color="error" onClick={handleCategoryDelete}>
                            <span>DELETE</span>
                            <DeleteForeverIcon className="s-24-i txt-white ml-16" />
                          </Button>
                        ) : (
                          <div className="ml-16">
                            <AreYouSureYesNo
                              onNo={categoryOnNo}
                              onYes={categoryOnYes}
                              loading={categoryDelete.isDeleting}
                              loadingMessage="Deleting category ..."
                              warning="THIS CATEGORY AND ALL THE PRODUCTS IN IT WILL BE DELETED!"
                            />
                          </div>
                        )}
                      </Grid>
                    )}
                  </Grid>
                </Form>
              </div>
            );
          }}
        </Formik>
      </Grid>
    </Grid>
  );
};

export default ManageCategory;
